
import React from 'react';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, X, RefreshCw, Eye } from 'lucide-react';
import { Appointment } from '@/types/dashboard';

interface AppointmentActionButtonsProps {
  appointment: Appointment;
  userRole: 'psychologist' | 'patient';
  onCancel: (appointmentId: string) => void;
  onReschedule: (appointmentId: string) => void;
  onViewDetails: (appointmentId: string) => void;
  disabled?: boolean;
}

/**
 * Componente que exibe botões de ação para gerenciar consultas
 * Permite cancelar, reagendar e visualizar detalhes de uma consulta
 */
const AppointmentActionButtons: React.FC<AppointmentActionButtonsProps> = ({
  appointment,
  userRole,
  onCancel,
  onReschedule,
  onViewDetails,
  disabled = false
}) => {
  // Verifica se a consulta pode ser cancelada com base no status
  const canBeCancelled = ['scheduled', 'pending_reschedule', 'confirmed'].includes(appointment.status);
  
  // Verifica se a consulta pode ser reagendada com base no status
  const canBeRescheduled = ['scheduled', 'confirmed'].includes(appointment.status);
  
  // Determina se os botões devem estar desabilitados
  const isDisabled = disabled || !appointment.id;

  return (
    <div className="flex justify-end space-x-2">
      <Button
        variant="outline"
        size="sm"
        onClick={() => onViewDetails(appointment.id)}
        disabled={isDisabled}
        title="Ver detalhes da consulta"
      >
        <Eye className="h-4 w-4 mr-1" />
        Detalhes
      </Button>
      
      {canBeRescheduled && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onReschedule(appointment.id)}
          disabled={isDisabled}
          title={userRole === 'psychologist' 
            ? 'Propor novo horário para esta consulta'
            : 'Solicitar reagendamento desta consulta'}
        >
          <RefreshCw className="h-4 w-4 mr-1" />
          Reagendar
        </Button>
      )}
      
      {canBeCancelled && (
        <Button
          variant="destructive"
          size="sm"
          onClick={() => onCancel(appointment.id)}
          disabled={isDisabled}
          title={userRole === 'psychologist' 
            ? 'Cancelar esta consulta (o paciente será notificado)'
            : 'Cancelar esta consulta (o psicólogo será notificado)'}
        >
          <X className="h-4 w-4 mr-1" />
          Cancelar
        </Button>
      )}
    </div>
  );
};

export default AppointmentActionButtons;
